import type { TripWorkspace } from "./trip";
const escapeCalendar = (s: string) =>
	s
		.replaceAll("\\", "\\\\")
		.replaceAll("\n", "\\n")
		.replaceAll(";", "\\;")
		.replaceAll(",", "\\,");
export function foldCalendarLine(line: string) {
	const parts: string[] = [];
	let current = "";
	for (const c of line) {
		if (new TextEncoder().encode(current + c).length > 75) {
			parts.push(current);
			current = " " + c;
		} else current += c;
	}
	parts.push(current);
	return parts.join("\r\n");
}
export function tripCalendar(
	trip: TripWorkspace,
	offsets: number[] = [7, 3, 1, 0],
) {
	const events: string[] = [];
	const stamp = new Date()
		.toISOString()
		.replaceAll(/[-:]/g, "")
		.replace(/\.\d{3}Z/, "Z");
	const d = (s: string) => s.replaceAll("-", "");
	for (const [i, day] of trip.days.entries()) {
		const alarms =
			i === 0
				? offsets.flatMap((offset) => [
						"BEGIN:VALARM",
						"ACTION:DISPLAY",
						`DESCRIPTION:${escapeCalendar("일본 여행 준비 확인")}`,
						`TRIGGER;VALUE=DATE-TIME:${new Date(
							Date.parse(
								`${trip.startDate}T${offset === 0 ? "07" : "09"}:00:00+09:00`,
							) -
								offset * 86400000,
						)
							.toISOString()
							.replaceAll(/[-:]/g, "")
							.replace(/\.\d{3}Z/, "Z")}`,
						"END:VALARM",
					])
				: [];
		const following = new Date(`${day.date}T00:00:00Z`);
		following.setUTCDate(following.getUTCDate() + 1);
		events.push(
			[
				"BEGIN:VEVENT",
				`UID:${trip.id}-${i}@japangolmok.local`,
				`DTSTAMP:${stamp}`,
				`DTSTART;VALUE=DATE:${d(day.date)}`,
				`DTEND;VALUE=DATE:${d(following.toISOString().slice(0, 10))}`,
				`SUMMARY:${escapeCalendar(`${trip.destinationCity} ${i + 1}일차`)}`,
				`DESCRIPTION:${escapeCalendar(day.items.map((p) => `${p.time ?? ""} ${p.title}`).join("\n"))}`,
				...alarms,
				"END:VEVENT",
			].join("\r\n"),
		);
		for (const item of day.items.filter((p) => p.time)) {
			const time = item.time!.replace(":", "") + "00";
			events.push(
				[
					"BEGIN:VEVENT",
					`UID:${trip.id}-${item.id}@japangolmok.local`,
					`DTSTAMP:${stamp}`,
					`DTSTART;TZID=Asia/Tokyo:${d(day.date)}T${time}`,
					`SUMMARY:${escapeCalendar(item.title)}`,
					"END:VEVENT",
				].join("\r\n"),
			);
		}
	}
	return (
		[
			"BEGIN:VCALENDAR",
			"VERSION:2.0",
			"PRODID:-//japangolmok//Travel//KO",
			"CALSCALE:GREGORIAN",
			"BEGIN:VTIMEZONE",
			"TZID:Asia/Tokyo",
			"BEGIN:STANDARD",
			"DTSTART:19700101T000000",
			"TZOFFSETFROM:+0900",
			"TZOFFSETTO:+0900",
			"TZNAME:JST",
			"END:STANDARD",
			"END:VTIMEZONE",
			...events,
			"END:VCALENDAR",
		]
			.flatMap((l) => l.split("\r\n").map(foldCalendarLine))
			.join("\r\n") + "\r\n"
	);
}
export function leaveForAirport(time: string, minutes: number, buffer: number) {
	if (
		!/^([01]\d|2[0-3]):[0-5]\d$/.test(time) ||
		![minutes, buffer].every((n) => Number.isInteger(n) && n >= 0 && n <= 1440)
	)
		throw new Error("INVALID_RETURN_TIME");
	let n =
		Number(time.slice(0, 2)) * 60 + Number(time.slice(3)) - minutes - buffer;
	const prior = n < 0;
	n = ((n % 1440) + 1440) % 1440;
	return `${prior ? "전날 " : ""}${String(Math.floor(n / 60)).padStart(2, "0")}:${String(n % 60).padStart(2, "0")}`;
}
export function preparationDue(trip: TripWorkspace, today: string) {
	return trip.checklist
		.filter((c) => !c.done)
		.map((c) => {
			const offset =
				c.dueOffsetDays ??
				(
					{
						passport: 30,
						"visit-japan-web": 7,
						insurance: 7,
						payment: 3,
						connectivity: 3,
						offline: 1,
						"route-refresh": 3,
					} as Record<string, number>
				)[c.id] ??
				1;
			const date = new Date(`${trip.startDate}T00:00:00Z`);
			date.setUTCDate(date.getUTCDate() - offset);
			return { ...c, offset, due: date.toISOString().slice(0, 10) };
		})
		.map((c) => ({
			...c,
			overdue: c.due < today,
			withinWeek: (Date.parse(c.due) - Date.parse(today)) / 86400000 <= 7,
		}));
}
